// Co-branding mark for the app chrome (PRD-55 §FR-4, §FR-6).
//
// Renders the school's crest and name where the Platonica wordmark used
// to stand alone, with Platonica moved to the second line. That
// inversion is the point of the feature: the professor's school leads,
// and Platonica is the tool it runs on.
//
// Three details here are deliberate; each has a failure mode behind it.
//
// 1. FIXED LIGHT CHIP in both themes. A dark-navy crest — very common on
//    Mexican school shields — is a black rectangle on the sidebar's
//    `dark:bg-slate-900/80`. Rendering it on white in both themes is a
//    slight aesthetic compromise in dark mode and the only thing that
//    keeps it legible. The alternative (a second, dark-mode logo column)
//    doubles the upload UI, the storage and the resolver.
//
// 2. NOT A SQUARE. School crests are as often 4:1 wordmarks as shields;
//    a wordmark in the old `h-9 w-9` tile renders as a 36x9px smear.
//    Fixed height, flexible width, `object-contain`.
//
// 3. The BOX IS RESERVED before the image loads, and the Platonica mark
//    sits underneath until then. Without that the header jumps on every
//    cold load — the professor's first paint, and a student's portal
//    load on a phone.
//
// The school NAME renders even with no crest: it needs no upload, it is
// already on the client in every authenticated render, and it
// personalizes the 100% of schools that never upload one.

import { cn } from "@lib/cn"
import { Sparkles } from "lucide-react"
import { useState } from "react"

interface Props {
  /** Resolved crest URL, or null when the tenant has none. */
  logoUrl?: string | null
  /** School name. Falls back to the Platonica wordmark when absent. */
  schoolName?: string | null
  /** Second line — "Platonica · Copiloto Docente" in the sidebar. */
  subtitle?: string
  /** Platonica's own wordmark, shown when there is no school name. */
  fallbackName: string
  /** Compact variant for the mobile top bar. */
  compact?: boolean
  className?: string
}

export function SchoolMark({
  logoUrl,
  schoolName,
  subtitle,
  fallbackName,
  compact,
  className,
}: Props) {
  const [failed, setFailed] = useState(false)
  const showLogo = !!logoUrl && !failed

  return (
    <div className={cn("flex items-center gap-3", className)}>
      <div
        className={cn(
          // The box is reserved whether or not a logo loads, so nothing
          // reflows when it arrives.
          "flex shrink-0 items-center justify-center rounded-lg",
          compact ? "h-8" : "h-9",
          showLogo
            ? // Light chip in BOTH themes — see note 1 above. Do not add
              // a `dark:` variant here; a dark crest disappears.
              cn("bg-white p-1", compact ? "w-auto max-w-[5rem]" : "w-auto max-w-[7rem]")
            : "bg-ink text-ink-inverted w-9",
        )}
      >
        {showLogo ? (
          <img
            src={logoUrl}
            // Decorative: the school's name is rendered as text right
            // beside it, so announcing it here would read it twice.
            alt=""
            aria-hidden="true"
            className="max-h-full max-w-full object-contain"
            onError={() => setFailed(true)}
          />
        ) : (
          <Sparkles className={compact ? "h-4 w-4" : "h-5 w-5"} aria-hidden="true" />
        )}
      </div>
      <div className="min-w-0 leading-tight">
        <p className={cn("text-ink truncate font-bold", compact ? "text-sm" : "text-sm")}>
          {schoolName || fallbackName}
        </p>
        {!compact && subtitle && <p className="text-ink-subtle truncate text-xs">{subtitle}</p>}
      </div>
    </div>
  )
}
